import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { msalConfig } from './config/authConfig';
import { rememberLoginHint } from './services/silentSignIn';
import { ThemeProvider, ThemedFluentProvider } from './theme/ThemeContext';
import './i18n';
import './index.css';
import { installGlobalErrorCapture } from './telemetry/globalErrorCapture';

// Uncaught errors feed the LOCAL client-error ring, which the feedback widget attaches to a report (nothing is sent anywhere
// else). Installed before anything renders, so an error during start-up is caught too.
installGlobalErrorCapture();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

export const msalInstance = new PublicClientApplication(msalConfig);

async function initializeMsal() {
  await msalInstance.initialize();

  const response = await msalInstance.handleRedirectPromise();
  if (response) {
    msalInstance.setActiveAccount(response.account);
    rememberLoginHint(response.account);
  } else {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as { account: unknown };
      // @ts-expect-error MSAL payload shape at runtime
      msalInstance.setActiveAccount(payload.account);
      // @ts-expect-error MSAL payload shape at runtime
      rememberLoginHint(payload.account);
    }
  });

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <ThemedFluentProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </ThemedFluentProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </MsalProvider>
    </React.StrictMode>
  );
}

initializeMsal().catch(console.error);
